import React, { useState, useEffect, useCallback } from 'react';

// Trailer Burndown — 5-day container burndown from a chosen start date.
//   Day 1 Containers Available = live FALLIN onsite counts by path.
//   Scheduled Drops = manual daily total, split by the LVP FPP % (overridable).
//   Onsite + Deliveries = Available + Scheduled Drops.
//   Process Plan (per shift) = LVP plan cartons by path/shift / avg trailer density.
//   Next day Available = prior (Onsite+Deliveries) - (day + night process plan).

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const PATHS = ['PID', 'PR', 'AROS', 'SKID'];
// FALLIN yard paths use MANR where this view uses AROS.
const YARD_PATH = { PID: 'PID', PR: 'PR', AROS: 'MANR', SKID: 'SKID' };
const FPP_KEY = { PID: 'pid', PR: 'pr', AROS: 'aros', SKID: 'skid' };

export default function TrailerBurndown({ api, site }) {
  const [startDate, setStartDate] = useState('');
  const [density, setDensity] = useState('');            // avg trailer density (cartons/trailer)
  const [drops, setDrops] = useState({});                // { 'YYYY-MM-DD': { total, pid, pr, aros, skid (overrides) } }
  const [yardAvail, setYardAvail] = useState(null);      // { PID, PR, AROS, SKID } live from FALLIN (button source)
  const [onsite, setOnsite] = useState({});              // Day-1 available, editable + static until "bring in" pressed
  const [procOv, setProcOv] = useState({});              // process-plan overrides: { 'date|shift|path': value }
  const [lvpWeeks, setLvpWeeks] = useState({});          // { weekStart: lvpData }
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(false);
  // Unified Portal auto-load: per-EAD-day count of ARRIVAL_SCHEDULED drops,
  // used as the default Scheduled Drops total when a day has no manual entry.
  const [upScheduled, setUpScheduled] = useState({}); // { 'YYYY-MM-DD': count }

  // 5 consecutive dates from startDate
  const dates = (() => {
    if (!startDate) return [];
    const out = [];
    const d0 = new Date(startDate + 'T12:00:00');
    for (let i = 0; i < 5; i++) {
      const d = new Date(d0); d.setDate(d0.getDate() + i);
      out.push(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`);
    }
    return out;
  })();

  const weekStartFor = (dateStr) => {
    const d = new Date(dateStr + 'T12:00:00');
    const sun = new Date(d); sun.setDate(d.getDate() - d.getDay());
    return `${sun.getFullYear()}-${String(sun.getMonth()+1).padStart(2,'0')}-${String(sun.getDate()).padStart(2,'0')}`;
  };

  // On mount, default to the most recently saved burndown (pointer key), so the
  // tab opens on the latest plan instead of waiting for a manual date pick.
  useEffect(() => {
    fetch(`${api}/api/fclm/cache?site=${site}&date=TRAILER_BURNDOWN_LATEST&shift_type=ptr`)
      .then(r => r.json())
      .then(d => { if (d && d.startDate) setStartDate(d.startDate); })
      .catch(() => {});
  }, [api, site]);

  // --- Load live yard (FALLIN) + saved burndown + LVP weeks the range touches ---
  useEffect(() => {
    setLoading(true);
    // Live yard: count IBVEND trailers by path (same rule as FALLIN).
    fetch(`${api}/api/fclm/cache?site=${site}&date=YMS_YARD&shift_type=current`)
      .then(r => r.json())
      .then(y => {
        const trailers = (y?.trailers) || [];
        const avail = {};
        PATHS.forEach(p => {
          const yp = YARD_PATH[p];
          avail[p] = trailers.filter(t => t.path === yp && !t.isDefect && t.notes && t.notes.toUpperCase().startsWith('IBVEND')).length;
        });
        setYardAvail(avail);
      })
      .catch(() => setYardAvail(null))
      .finally(() => setLoading(false));
  }, [api, site]);

  useEffect(() => {
    if (!startDate) return;
    // Saved burndown inputs for this start date.
    fetch(`${api}/api/fclm/cache?site=${site}&date=TRAILER_BURNDOWN_${startDate}&shift_type=plan`)
      .then(r => r.json())
      .then(d => {
        if (d && (d.drops || d.density || d.onsite)) {
          setDrops(d.drops || {});
          if (d.density != null && d.density !== '') setDensity(String(d.density));
          if (d.onsite) setOnsite(d.onsite);
          setProcOv(d.procOv || {});
        }
      }).catch(() => {});
  }, [api, site, startDate]);

  // Fetch every LVP week the 5-day range touches.
  useEffect(() => {
    if (!dates.length) return;
    const weeks = [...new Set(dates.map(weekStartFor))];
    Promise.all(weeks.map(w =>
      fetch(`${api}/api/fclm/cache?site=${site}&date=LVP_${w}&shift_type=plan`).then(r => r.json())
        .then(d => ({ w, d })).catch(() => ({ w, d: null }))
    )).then(res => {
      const m = {}; res.forEach(({ w, d }) => { m[w] = d; });
      setLvpWeeks(m);
    });
  }, [api, site, startDate]);

  // Load Unified Portal appointments once; build a per-EAD-day count of
  // ARRIVAL_SCHEDULED *drops* to auto-fill Scheduled Drops (editable override).
  useEffect(() => {
    fetch(`${api}/api/fclm/cache?site=${site}&date=UNIFIED_PORTAL_${site}&shift_type=data`)
      .then(r => r.json())
      .then(d => {
        const appts = (d && Array.isArray(d.appts)) ? d.appts : [];
        const by = {};
        for (const a of appts) {
          if (!a.isDrop) continue;
          if ((a.status || '').toUpperCase() !== 'ARRIVAL_SCHEDULED') continue;
          if (!a.eadDate) continue;
          by[a.eadDate] = (by[a.eadDate] || 0) + 1;
        }
        setUpScheduled(by);
      })
      .catch(() => setUpScheduled({}));
  }, [api, site]);

  const save = async () => {
    await fetch(`${api}/api/fclm/cache`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ site, date: `TRAILER_BURNDOWN_${startDate}`, shift_type: 'plan', data: { drops, density, onsite, procOv } }),
    });
    // Pointer to the most recently saved burndown so the tab defaults to it on load.
    await fetch(`${api}/api/fclm/cache`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ site, date: 'TRAILER_BURNDOWN_LATEST', shift_type: 'ptr', data: { startDate } }),
    }).catch(() => {});
    setSaved(true); setTimeout(() => setSaved(false), 3000);
  };

  const dens = parseFloat(density) || 0;

  // LVP FPP ratios for the START date's week (used to split scheduled-drop totals).
  const startFpp = (() => {
    const lvp = lvpWeeks[weekStartFor(startDate)];
    return (lvp && lvp.fppRatios) || { pid: 0.6, aros: 0.15, pr: 0.16, skid: 0.09 };
  })();

  // Scheduled drops split by path (FPP of the total, unless overridden per path).
  // Effective Scheduled Drops total for a day: manual entry if present,
  // otherwise the Unified Portal ARRIVAL_SCHEDULED auto count (editable override).
  const effectiveTotal = (dateStr) => {
    const row = drops[dateStr] || {};
    if (row.total !== undefined && row.total !== '') return parseInt(row.total) || 0;
    return upScheduled[dateStr] || 0;
  };

  const dropSplit = (dateStr, path) => {
    const row = drops[dateStr] || {};
    const ov = row[FPP_KEY[path]];
    if (ov !== undefined && ov !== '') return parseInt(ov) || 0;
    const total = effectiveTotal(dateStr);
    return Math.round(total * (startFpp[FPP_KEY[path]] || 0));
  };

  // LVP plan cartons for a given date + shift + path (uses that date's own week).
  const planCartons = (dateStr, shift, path) => {
    const lvp = lvpWeeks[weekStartFor(dateStr)];
    if (!lvp || !lvp.shiftData) return 0;
    const dayName = DAYS[new Date(dateStr + 'T12:00:00').getDay()];
    const sd = lvp.shiftData[`${dayName}_${shift}`] || {};
    const total = sd.cartons || 0;
    const fpp = lvp.fppRatios || startFpp;
    if (path === 'PID') return (sd.pidCartons !== undefined && sd.pidCartons !== '') ? (parseInt(sd.pidCartons) || 0) : Math.round(total * (fpp.pid || 0));
    if (path === 'AROS') return (sd.arosCartons !== undefined && sd.arosCartons !== '') ? (parseInt(sd.arosCartons) || 0) : Math.round(total * (fpp.aros || 0));
    return Math.round(total * (fpp[FPP_KEY[path]] || 0));
  };

  // Process plan = plan cartons / density, rounded (trailers that shift processes).
  // A manual per-cell override wins when present.
  const computedProcessTrailers = (dateStr, shift, path) => {
    if (dens <= 0) return 0;
    return Math.round(planCartons(dateStr, shift, path) / dens);
  };
  const processTrailers = (dateStr, shift, path) => {
    const ov = procOv[`${dateStr}|${shift}|${path}`];
    if (ov !== undefined && ov !== '') return parseInt(ov) || 0;
    return computedProcessTrailers(dateStr, shift, path);
  };

  // --- The burndown recursion: available[day][path] ---------------------------
  const available = (() => {
    const avail = {};
    dates.forEach((dt, i) => {
      avail[dt] = {};
      PATHS.forEach(p => {
        if (i === 0) {
          avail[dt][p] = parseInt(onsite[p]) || 0;   // Day 1 = editable onsite (populated via button)
        } else {
          const prev = dates[i - 1];
          const prevOnsitePlusDrops = (avail[prev][p] || 0) + dropSplit(prev, p);
          const processed = processTrailers(prev, 'day', p) + processTrailers(prev, 'night', p);
          avail[dt][p] = Math.max(0, prevOnsitePlusDrops - processed);
        }
      });
    });
    return avail;
  })();

  const onsitePlusDrops = (dt, p) => (available[dt]?.[p] || 0) + dropSplit(dt, p);

  // --- styling ---
  const cell = { border: '1px solid #ccc', padding: '3px 8px', fontSize: '0.72rem', textAlign: 'right', whiteSpace: 'nowrap' };
  const lbl = { ...cell, textAlign: 'left', fontWeight: 600, color: '#c47b17' };
  const hdr = { ...cell, background: '#f0f0f0', fontWeight: 700, textAlign: 'center', color: '#232f3e' };
  const yellow = '#fffec8';
  const dow = (dt) => DAYS[new Date(dt + 'T12:00:00').getDay()];
  const setDrop = (dt, field, val) => setDrops(prev => ({ ...prev, [dt]: { ...(prev[dt] || {}), [field]: val } }));
  const bringInCurrent = () => { if (yardAvail) setOnsite({ ...yardAvail }); };
  const setOnsiteCell = (p, val) => setOnsite(prev => ({ ...prev, [p]: val }));
  const setProc = (dt, shift, p, val) => setProcOv(prev => ({ ...prev, [`${dt}|${shift}|${p}`]: val }));

  const DateHeaderRow = ({ label }) => (
    <tr><th style={hdr}>{label}</th>{dates.map(dt => <th key={dt} style={hdr}>{dow(dt)}<br/>{dt.replace(/-/g,'/')}</th>)}</tr>
  );

  return (
    <div>
      <style>{`.no-spin::-webkit-outer-spin-button,.no-spin::-webkit-inner-spin-button{-webkit-appearance:none;margin:0;} .no-spin{-moz-appearance:textfield;} .tb-table{table-layout:fixed;width:520px;} .tb-table td:first-child,.tb-table th:first-child{width:150px;}`}</style>
      <h2 className="page-title">Trailer Burndown</h2>
      <div className="card" style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <label style={{ fontSize: '0.75rem' }}>Burndown Start Date: <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ fontSize: '0.75rem' }} /></label>
        <label style={{ fontSize: '0.75rem' }}>Avg Trailer Density (cartons/trailer): <input type="number" value={density} onChange={e => setDensity(e.target.value)} placeholder="e.g. 400" className="no-spin" style={{ width: 90, fontSize: '0.75rem', background: yellow }} /></label>
        <button className="btn btn-primary" onClick={save} disabled={!startDate} style={{ fontSize: '0.7rem', padding: '4px 10px' }}>{saved ? '✅ Saved!' : '💾 Save'}</button>
        <button className="btn" onClick={bringInCurrent} disabled={!yardAvail} style={{ background: '#2e7d32', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: '0.7rem' }}>⬇️ Bring in current containers</button>
        {yardAvail && <span style={{ fontSize: '0.68rem', color: '#888' }}>FALLIN now: {PATHS.reduce((s,p)=>s+(yardAvail[p]||0),0)} total</span>}
        {loading && <span style={{ fontSize: '0.7rem', color: '#888' }}>Loading…</span>}
      </div>

      {!startDate && <div className="card">Set a burndown start date to begin.</div>}

      {startDate && (
        <>
          {/* Row 1: Containers Available (left) + Scheduled Drops (right) */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div className="card" style={{ overflowX: 'auto' }}>
              <table className="tb-table" style={{ borderCollapse: 'collapse' }}>
                <thead><DateHeaderRow label="Onsite Containers" /></thead>
                <tbody>
                  <tr><td style={lbl}>Containers Available</td>{dates.map((dt,i) => <td key={dt} style={{ ...cell, fontWeight: 700, background: i===0 ? '#eef6ff' : undefined }}>{PATHS.reduce((s,p)=>s+(available[dt]?.[p]||0),0).toLocaleString()}</td>)}</tr>
                  {PATHS.map(p => <tr key={p}><td style={{ ...lbl, paddingLeft: 16, color: '#555', fontWeight: 500 }}>{p}</td>{dates.map((dt,i) => i===0
                    ? <td key={dt} style={{ ...cell, padding: 1, background: yellow }}><input type="number" value={onsite[p] ?? ''} onChange={e => setOnsiteCell(p, e.target.value)} placeholder="0" className="no-spin" style={{ width: 55, border: 'none', background: 'transparent', textAlign: 'right', fontSize: '0.72rem' }} /></td>
                    : <td key={dt} style={cell}>{(available[dt]?.[p]||0).toLocaleString()}</td>)}</tr>)}
                </tbody>
              </table>
              {Object.keys(onsite).length === 0 && <p style={{ fontSize: '0.65rem', color: '#888' }}>Click "Bring in current containers" to populate Day 1 from FALLIN, or type values in. Values stay static until you re-press the button.</p>}
            </div>

            <div className="card" style={{ overflowX: 'auto' }}>
              <table className="tb-table" style={{ borderCollapse: 'collapse' }}>
                <thead><DateHeaderRow label="Scheduled Drops" /></thead>
                <tbody>
                  <tr><td style={lbl}>Scheduled Drops</td>{dates.map(dt => { const manual=(drops[dt]||{}).total; const hasManual=manual!==undefined&&manual!==''; const auto=upScheduled[dt]||0; return <td key={dt} style={{ ...cell, background: yellow, padding: 1 }} title={!hasManual&&auto?`Auto from Unified Portal (Arrival Scheduled): ${auto}. Type to override.`:undefined}><input type="number" value={(drops[dt]||{}).total ?? ''} onChange={e => setDrop(dt,'total',e.target.value)} placeholder={String(auto)} className="no-spin" style={{ width: 60, border: 'none', background: 'transparent', textAlign: 'right', fontSize: '0.72rem', color: hasManual?'#000':'#1565c0', fontStyle: hasManual?'normal':'italic' }} /></td>; })}</tr>
                  {PATHS.map(p => <tr key={p}><td style={{ ...lbl, paddingLeft: 16, color: '#555', fontWeight: 500 }}>{p}</td>{dates.map(dt => { const row=drops[dt]||{}; const ov=row[FPP_KEY[p]]; return <td key={dt} style={{ ...cell, padding: 1, background: (ov!==undefined&&ov!=='') ? '#fff3e0' : undefined }}><input type="number" value={ov ?? ''} onChange={e => setDrop(dt,FPP_KEY[p],e.target.value)} placeholder={String(dropSplit(dt,p))} className="no-spin" style={{ width: 55, border: 'none', background: 'transparent', textAlign: 'right', fontSize: '0.72rem' }} /></td>; })}</tr>)}
                </tbody>
              </table>
              <p style={{ fontSize: '0.62rem', color: '#888', marginTop: 4 }}><span style={{ color: '#1565c0', fontStyle: 'italic' }}>Blue italic</span> Scheduled Drops auto-fill from the Unified Portal (Arrival Scheduled drops by EAD day); type to override. Per-path splits auto-fill from the start-date LVP FPP ({PATHS.map(p=>`${p} ${Math.round((startFpp[FPP_KEY[p]]||0)*100)}%`).join(' / ')}); type to override.</p>
            </div>
          </div>

          {/* Row 2: Total Containers (Onsite + Deliveries) */}
          <div className="card" style={{ overflowX: 'auto', marginTop: 8 }}>
            <table className="tb-table" style={{ borderCollapse: 'collapse' }}>
              <thead><DateHeaderRow label="Total Containers" /></thead>
              <tbody>
                <tr><td style={lbl}>Onsite + Deliveries</td>{dates.map(dt => <td key={dt} style={{ ...cell, fontWeight: 700 }}>{PATHS.reduce((s,p)=>s+onsitePlusDrops(dt,p),0).toLocaleString()}</td>)}</tr>
                {PATHS.map(p => <tr key={p}><td style={{ ...lbl, paddingLeft: 16, color: '#555', fontWeight: 500 }}>{p}</td>{dates.map(dt => <td key={dt} style={cell}>{onsitePlusDrops(dt,p).toLocaleString()}</td>)}</tr>)}
              </tbody>
            </table>
          </div>

          {/* Row 3: Process Plan by shift (Day left, Night right) */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8 }}>
            {['day','night'].map(shift => (
              <div key={shift} className="card" style={{ overflowX: 'auto' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#232f3e', marginBottom: 4 }}>{shift === 'day' ? 'Day Shift' : 'Night Shift'} — Process Plan</div>
                <table className="tb-table" style={{ borderCollapse: 'collapse' }}>
                  <thead>
                    <tr><th style={hdr}>Process Plan</th>{dates.map(dt => <th key={dt} style={hdr}>{dow(dt)}<br/>{dt.replace(/-/g,'/')}</th>)}</tr>
                  </thead>
                  <tbody>
                    <tr><td style={{ ...lbl, color: '#232f3e' }}># of Containers</td>{dates.map(dt => <td key={dt} style={{ ...cell, fontWeight: 700 }}>{PATHS.reduce((s,p)=>s+processTrailers(dt,shift,p),0).toLocaleString()}</td>)}</tr>
                    {PATHS.map(p => <tr key={p}><td style={{ ...lbl, paddingLeft: 16, color: '#555', fontWeight: 500 }}>{p}</td>{dates.map(dt => { const k=`${dt}|${shift}|${p}`; const ov=procOv[k]; return <td key={dt} style={{ ...cell, padding: 1, background: (ov!==undefined&&ov!=='') ? '#fff3e0' : undefined }}><input type="number" value={ov ?? ''} onChange={e => setProc(dt,shift,p,e.target.value)} placeholder={String(computedProcessTrailers(dt,shift,p))} className="no-spin" style={{ width: 55, border: 'none', background: 'transparent', textAlign: 'right', fontSize: '0.72rem' }} /></td>; })}</tr>)}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
          {dens <= 0 && <p style={{ fontSize: '0.7rem', color: '#c62828', marginTop: 6 }}>Set an avg trailer density above to compute the Process Plan (trailers = plan cartons ÷ density).</p>}
        </>
      )}
    </div>
  );
}
